import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"

import {
  getAccessoriesCompanyList,
  getAccessoriesCompanyById,
  createAccessoriesCompany,
  updateAccessoriesCompany,
  deleteAccessoriesCompany,
  getTrashedAccessoriesCompanies,
  restoreAccessoriesCompany,
  forceDeleteAccessoriesCompany,
  getAccessoriesCompanyBySlug,
} from "api/accessoriesCompanyApi"

import {
  useIsFetching,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

export const ACCESSORIES_COMPANY_QUERY_KEY = "accessories-companies"

// -------------------- FETCH PAGINATED --------------------
export const useAccessoriesCompanies = (params = {}, enabled = true) => {
  return usePaginatedResource(
    ACCESSORIES_COMPANY_QUERY_KEY,
    getAccessoriesCompanyList,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useAccessoriesCompany = (id, enabled = true) => {
  return useResourceItem(
    ACCESSORIES_COMPANY_QUERY_KEY,
    getAccessoriesCompanyById,
    id,
    enabled
  )
}

// -------------------- FETCH BY SLUG --------------------
export const useAccessoriesCompanyBySlug = (slug, enabled = true) => {
  return useResourceItem(
    [ACCESSORIES_COMPANY_QUERY_KEY, "slug"],
    getAccessoriesCompanyBySlug,
    slug,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateAccessoriesCompany = () => {
  return useCreateResource(
    createAccessoriesCompany,
    ACCESSORIES_COMPANY_QUERY_KEY
  )
}

// -------------------- EDIT --------------------
export const useEditAccessoriesCompany = () => {
  return useEditResource(
    updateAccessoriesCompany,
    ACCESSORIES_COMPANY_QUERY_KEY
  )
}

// -------------------- DELETE (Soft Delete) --------------------
export const useDeleteAccessoriesCompany = () => {
  return useDeleteResource(
    deleteAccessoriesCompany,
    ACCESSORIES_COMPANY_QUERY_KEY
  )
}

// -------------------- FORCE DELETE (Permanent Delete) --------------------
export const useForceDeleteAccessoriesCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: forceDeleteAccessoriesCompany,
    onSuccess: (_, id) => {
      // Remove from trashed cache
      queryClient.setQueriesData(
        [ACCESSORIES_COMPANY_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(
            item => item.id !== id
          )
          updated.data.total = Math.max(
            0,
            (updated.data.total || 1) - 1
          )
          return updated
        }
      )

      // Remove individual cache
      queryClient.removeQueries([ACCESSORIES_COMPANY_QUERY_KEY, id])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// ✅ Get all (no pagination)
export const useAllAccessoriesCompanies = (params = {}) => {
  return usePaginatedResource(
    ACCESSORIES_COMPANY_QUERY_KEY,
    getAccessoriesCompanyList,
    { ...params, all: true },
    true
  )
}

// ✅ Get trashed (soft-deleted)
export const useTrashedAccessoriesCompanies = (params = {}) => {
  return usePaginatedResource(
    [ACCESSORIES_COMPANY_QUERY_KEY, "trashed"],
    getTrashedAccessoriesCompanies,
    params,
    true
  )
}

// ✅ Restore trashed item
export const useRestoreAccessoriesCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: restoreAccessoriesCompany,
    onSuccess: (res, id) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Remove from trashed cache
      queryClient.setQueriesData(
        [ACCESSORIES_COMPANY_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(
            item => item.id !== id
          )
          updated.data.total = Math.max(
            0,
            (updated.data.total || 1) - 1
          )
          return updated
        }
      )

      // Add to main list cache
      queryClient.setQueriesData(
        [ACCESSORIES_COMPANY_QUERY_KEY],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = [restoredItem, ...updated.data.data]
          updated.data.total = (updated.data.total || 0) + 1
          return updated
        }
      )

      // Update individual cache
      queryClient.setQueryData(
        [ACCESSORIES_COMPANY_QUERY_KEY, id],
        restoredItem
      )
    },
  })
}

// -------------------- SEARCH (PROGRAMMATIC) --------------------
export const useAccessoriesCompanySearchAPI = ({
  all = true,
  limit = 5,
} = {}) => {
  const queryClient = useQueryClient()

  const accessoriesCompanySearchAPI = async searchTerm => {
    const queryKey = [
      "accessories-company-searching",
      { search: searchTerm, all, limit },
    ]

    const cached = queryClient.getQueryData(queryKey)
    if (cached) return cached

    const data = await queryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        getAccessoriesCompanyList({
          search: searchTerm,
          all: true,
        }),
    })

    return data
  }

  const loading =
    useIsFetching({
      queryKey: ["accessories-company-searching"],
    }) > 0

  return { accessoriesCompanySearchAPI, loading }
}
